import { Link } from "@inertiajs/react"
import { AlertTriangle, CheckCircle2, Timer, Warehouse } from "lucide-react"
import type { LucideIcon } from "lucide-react"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import type { FleetMetrics } from "@/types"

// The headline row. These are the four numbers the customer reads off their own
// Power BI page every morning, so each tile is a STAT TILE (label, value, one
// line of context) rather than a chart — the number IS the chart.
//
// Every tile is a link into the work-order queue filtered the way the tile
// counted, because "157 bad ordered not in shop" is only useful if the next
// click is the list of 157.

interface TileProps {
  label: string
  value: string
  caption: string
  href: string
  icon: LucideIcon
  testId: string
}

function StatTile({
  label,
  value,
  caption,
  href,
  icon: Icon,
  testId,
}: TileProps) {
  return (
    <Card className="hover:border-foreground/20 transition-colors">
      <Link href={href} className="block" data-testid={testId}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{label}</CardTitle>
          <Icon className="text-muted-foreground h-4 w-4" aria-hidden="true" />
        </CardHeader>
        <CardContent>
          {/* Proportional figures: a large standalone value looks loose in
              tabular-nums. Table columns opt into tabular separately. */}
          <div className="text-3xl font-semibold">{value}</div>
          <p className="text-muted-foreground mt-1 text-xs">{caption}</p>
        </CardContent>
      </Link>
    </Card>
  )
}

// A metric with no data reads as "—", never as 0 — "no pressure car finished a
// shop visit this year" and "pressure cars turn around in zero days" are
// opposite facts and must not render identically.
function dash(value: number | null | undefined, suffix = ""): string {
  return value === null || value === undefined ? "—" : `${value}${suffix}`
}

export function FleetStatTiles({ metrics }: { metrics: FleetMetrics }) {
  const routes = useOrgRoutes()
  const queue = (query: string) => `${routes.workOrdersPath()}?${query}`

  const gp = metrics.avg_days_in_shop?.general_purpose ?? null
  const hp = metrics.avg_days_in_shop?.pressure ?? null

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatTile
        testId="tile-bad-orders-not-in-shop"
        label="Bad ordered, not in shop"
        value={metrics.bad_orders_not_in_shop.toLocaleString()}
        caption={`${metrics.bad_orders_30_plus_days.toLocaleString()} waiting 30+ days`}
        href={queue("type=BadOrder&state=open&in_shop=false")}
        icon={AlertTriangle}
      />
      <StatTile
        testId="tile-cars-in-shop"
        label="Cars in shop"
        value={metrics.cars_in_shop.toLocaleString()}
        caption={`${metrics.cars_in_shop_90_plus_days.toLocaleString()} there 90+ days`}
        href={queue("state=open&in_shop=true")}
        icon={Warehouse}
      />
      <StatTile
        testId="tile-avg-days-in-shop"
        label="Avg days in shop"
        value={dash(gp)}
        caption={`General purpose · pressure ${dash(hp)}`}
        href={queue("state=closed&in_shop=true")}
        icon={Timer}
      />
      <StatTile
        testId="tile-fleet-availability"
        label="Fleet availability"
        value={dash(metrics.fleet_availability_pct, "%")}
        caption="Active cars with nothing open"
        href={routes.carsPath()}
        icon={CheckCircle2}
      />
    </div>
  )
}
